import React from 'react';
import { useContextSelector } from 'use-context-selector';
import { ModalGenericRules } from '../../../../components/ModalGenericRules';
import { CartContext } from '../../../../contexts/CartContext';
import { ModalsContext } from '../../../../contexts/ModalsContext';
import { PatientContext, PatientProps } from '../../../../contexts/PatientContext';
import { PaymentContext } from '../../../../contexts/PaymentContext';
import { RouterContext } from '../../../../contexts/RouterContext';
import { ScheduleContext } from '../../../../contexts/ScheduleContext';
import { ResponseGenericModal, UnitsAndResources } from '../../../../hooks/useGenericModal';
import { usePatientModule } from '../../../../hooks/usePatientModule';
import { ProductItemProps, Rule } from '../../../../models/Product';
import { ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { ServiceItemProps } from '../../../../models/Service';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import {
  INFO_GENERIC_RULES_ACTION,
  INFO_GENERIC_RULES_CONDITION,
  INFO_GENERIC_RULES_WHEN
} from '../../../../utils/constants';
import { handleGenericModals, verifyGenericModalCondition } from '../../../../utils/genericModals';
import { useProducts } from '../../../../flows/APS/store/Products';
import { fetchProductById } from '../../../../modules/products';
import { DoctorsWithoutSlotsModal } from '../../../../flows/APS/components/DoctorsWithoutSlotsModal';
import { Resources } from '../../../../models/ScheduleResource';

type ModalGenericProps = {
  genericModal: {
    genericModalCondition: string;
    setGenericModalCondition: React.Dispatch<React.SetStateAction<string>>;
    handleActionButtonModal(actionButton: string, data: any): Promise<ResponseGenericModal | undefined>;
    modalGenericVisible: boolean;
    setModalGenericVisible: React.Dispatch<React.SetStateAction<boolean>>;
    handleModalProps: (props: { condition?: string | undefined; visible: boolean }) => void;
  };

  resetCoverageSelection: () => void;
  handleRemoveResource: () => Promise<void>;
  patientSelected: PatientProps | undefined;
  selectedService: ServiceItemProps | undefined;
  setLoadingChangeProduct: (value: React.SetStateAction<boolean>) => void;
  setSpecificUnitsResources: React.Dispatch<React.SetStateAction<UnitsAndResources | undefined>>;
  onCloseModal?: () => void;
};

export const GenericModalActions: React.FC<ModalGenericProps> = ({
  genericModal,
  setLoadingChangeProduct,
  handleRemoveResource,
  selectedService,
  resetCoverageSelection,
  patientSelected,
  setSpecificUnitsResources,
  onCloseModal
}) => {
  const { goTo } = useContextSelector(RouterContext, state => state);
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { changeProduct } = useContextSelector(CartContext, state => state);
  const {
    selectedItem,
    changeSchedule,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  } = useContextSelector(ScheduleContext, state => state);
  const { setEligibilityPlans, eligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const { addPatientList } = useContextSelector(PatientContext, state => state);
  const { products: apsProducts } = useProducts();

  const { handleAddPatient, isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible } = usePatientModule({
    addPatientList,
    changeProduct,
    eligibilityPlans,
    setEligibilityPlans,
    goTo,
    changeSchedule,
    selectedSchedule: selectedItem,
    openModal,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  });

  function checkMandatoryModal({ productRules, genericModalWhen }: { productRules: Rule[]; genericModalWhen: string }) {
    const genericModals = handleGenericModals(productRules);
    if (genericModals) {
      const modal = verifyGenericModalCondition({
        genericModals: genericModals,
        condition: INFO_GENERIC_RULES_CONDITION.always,
        when: genericModalWhen
      });
      if (modal) {
        genericModal.handleModalProps({
          condition: ' INFO_GENERIC_RULES_CONDITION.always',
          visible: true
        });
        return true;
      }
    }
    return false;
  }

  const routeProductSelector = async ({
    product,
    showGenericModal,
    patient
  }: {
    product: ProductItemProps;
    showGenericModal?: boolean;
    patient: PatientProps;
  }) => {
    let mandatoryModal;
    if (product.rules && !showGenericModal) {
      mandatoryModal = checkMandatoryModal({
        productRules: product.rules,
        genericModalWhen: INFO_GENERIC_RULES_WHEN.after_choosing_service_type
      });
    }
    if (showGenericModal || !mandatoryModal) {
      const schedule = ScheduleItemPropsBuilder();
      const createdSchedule = schedule.setProduct(product);
      const hasModal = await handleAddPatient({
        patient: patient,
        productSelected: product,
        itemToSchedule: 'PRODUCT',
        schedule: createdSchedule
      });
      if (!showGenericModal) {
        if (hasModal?.type === INFO_GENERIC_RULES_CONDITION.is_first_appointment)
          genericModal.handleModalProps({
            condition: INFO_GENERIC_RULES_CONDITION.is_first_appointment,
            visible: true
          });
      }
    }
  };

  const openOtherProductToSchedule = async (newScheduleProduct: Resources) => {
    resetCoverageSelection();

    if (newScheduleProduct) {
      const catalogProduct = (await fetchProductById(newScheduleProduct?.id)).data;

      if (catalogProduct && patientSelected)
        await routeProductSelector({
          product: catalogProduct,
          showGenericModal: true,
          patient: patientSelected
        });
    }
  };

  const scheduleWithNurse = async () => {
    const newScheduleProduct = apsProducts.find(product => product.name.toLocaleUpperCase().includes('ENFERMEIRO'));

    if (newScheduleProduct) await openOtherProductToSchedule(newScheduleProduct);
    genericModal.setModalGenericVisible(false);
  };

  const changeProductListService = async (actionButton: string, data: any) => {
    setLoadingChangeProduct(true);

    const validateSchedule = await genericModal.handleActionButtonModal(actionButton, data);
    if (validateSchedule?.action === INFO_GENERIC_RULES_ACTION.FIND_ANOTHER_DOCTOR) {
      handleRemoveResource();
    }
    if (validateSchedule?.action === INFO_GENERIC_RULES_ACTION.CHANGE_PRODUCT)
      if (selectedService) await scheduleWithNurse();

    if (validateSchedule?.action === INFO_GENERIC_RULES_ACTION.FIND_SLOTS_BY_UNIT_SPECIALIST) {
      setSpecificUnitsResources(validateSchedule.data);
      handleRemoveResource();
    }
    if (validateSchedule?.action === INFO_GENERIC_RULES_ACTION.FIND_SLOTS_WITHOUT_RESOURCEID) {
      handleRemoveResource();
    }
    setLoadingChangeProduct(false);
  };

  return (
    <>
      {genericModal.genericModalCondition === INFO_GENERIC_RULES_CONDITION.doctors_unavailable ? (
        <DoctorsWithoutSlotsModal
          onClose={() => {
            onCloseModal && onCloseModal();
            genericModal.setModalGenericVisible(false);
          }}
          scheduleWithNurse={() => changeProductListService(INFO_GENERIC_RULES_ACTION.CHANGE_PRODUCT, '')}
          visible={genericModal.modalGenericVisible}
        />
      ) : (
        <ModalGenericRules
          when={INFO_GENERIC_RULES_WHEN.after_choosing_service_type}
          condition={genericModal.genericModalCondition}
          visible={genericModal.modalGenericVisible}
          isVisible={genericModal.setModalGenericVisible}
          handleActionButton={changeProductListService}
          onCloseModal={() => {
            onCloseModal && onCloseModal();
          }}
        />
      )}
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => {
          setIsModalSystemUnavailableVisible(false);
        }}
      />
    </>
  );
};
